import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { equals, prop } from 'ramda'
import { useMakeSelector, useSelector } from 'hooks/redux'
import { makeGetTemplatesSelector } from 'selectors/powerTakeOff'
import {
  DRAWER_APPLY_CUSTOM_TEMPLATE,
  DRAWER_APPLY_DAIMLER_TEMPLATE,
  DRAWER_SAVE_TEMPLATE,
} from 'constants/ui/ptoDiagram'
import { Row, Col, Paragraph } from 'components/patterns'
import { CREATE_PLUS_PTO_TEMPLATE } from 'constants/authorization'
import { hasRight } from 'selectors/authorization'

import { alertMessagePrefixByType } from 'components/PageMeta/utils/alert-style-helper'
import PtoSelectTemplateActionButtons from './PtoSelectTemplateActionButtons'
import {
  ApplyDaimlerTemplateWrapper,
  ApplyCustomTemplateWrapper,
  CreateNewTemplateWrapper,
  PageTitleWrapper,
  ViewWrapper,
  ApplyTemplateWrapper,
  ApplyTemplateFooter,
  CustomTemplateIcon,
  CustomTemplateIconWrapper,
  ApplyCustomTemplateTitle,
  StyledDaimlerLogoSvg,
  DaimlerLogoWrapper,
  ApplyDaimlerTemplateTitle,
  PageNoteWrapper,
} from './SelectPtoTemplateMode.style'

const DAIMLER_ORG_ID = '7'
const SelectPtoTemplateMode = ({
  onClose,
  isDrawerVisible,
  setDrawerTitle,
  setDrawerState,
}) => {
  useEffect(() => {
    setDrawerTitle('PTO Templates')
  }, [setDrawerTitle])

  const canCreatePTOTemplate = useSelector(hasRight, CREATE_PLUS_PTO_TEMPLATE)

  const handleDrawerAndClearTemplate = useCallback(() => {
    onClose()
  }, [onClose])

  const handleCreateNewTemplateWrapper = (newDrawerState) => {
    setDrawerState(newDrawerState)
  }

  const templates = useMakeSelector(makeGetTemplatesSelector)
  const [hasDTNA, hasCustom] = useMemo(() => {
    const temps = Object.values(templates)
    return [
      temps.some((tp) => equals(prop('orgId', tp), DAIMLER_ORG_ID)),
      temps.some((tp) => !equals(prop('orgId', tp), DAIMLER_ORG_ID)),
    ]
  }, [templates])

  return (
    <ViewWrapper>
      <Row>
        <Col span={24}>
          <PageTitleWrapper>
            {strings.select_pto_template_mode_title}
          </PageTitleWrapper>
          <PageNoteWrapper data-testid="apply-pto-template-replace-warning">
            <Paragraph bold={true} style={{ display: 'inline-block' }}>
              {alertMessagePrefixByType.info}
            </Paragraph>{' '}
            {strings.apply_pto_template_replace_vehicle_pto_configuration}
          </PageNoteWrapper>
          <ApplyTemplateWrapper>
            <ApplyDaimlerTemplateWrapper
              onClick={() =>
                handleCreateNewTemplateWrapper(DRAWER_APPLY_DAIMLER_TEMPLATE)
              }
              disabled={!hasDTNA}
              data-testid="apply-daimler-template"
            >
              <DaimlerLogoWrapper>
                <StyledDaimlerLogoSvg disabled={!hasDTNA} />
              </DaimlerLogoWrapper>

              <ApplyDaimlerTemplateTitle disabled={!hasDTNA}>
                {strings.select_pto_template_mode_daimler}
              </ApplyDaimlerTemplateTitle>
            </ApplyDaimlerTemplateWrapper>

            <ApplyCustomTemplateWrapper
              onClick={() =>
                handleCreateNewTemplateWrapper(DRAWER_APPLY_CUSTOM_TEMPLATE)
              }
              disabled={!hasCustom}
              data-testid="apply-custom-template"
            >
              <CustomTemplateIconWrapper>
                <CustomTemplateIcon
                  icon="custom-template"
                  disabled={!hasCustom}
                />
              </CustomTemplateIconWrapper>

              <ApplyCustomTemplateTitle disabled={!hasCustom}>
                {strings.select_pto_template_mode_custom}
              </ApplyCustomTemplateTitle>
            </ApplyCustomTemplateWrapper>

            <CreateNewTemplateWrapper
              disabled={!canCreatePTOTemplate}
              onClick={() =>
                handleCreateNewTemplateWrapper(DRAWER_SAVE_TEMPLATE)
              }
              data-testid="create-new-template"
            >
              <h2>+</h2>
              <div>{strings.select_pto_template_mode_create_new}</div>
            </CreateNewTemplateWrapper>
          </ApplyTemplateWrapper>
        </Col>
      </Row>
      <Row align="bottom">
        <Col span={24}>
          <ApplyTemplateFooter>
            <PtoSelectTemplateActionButtons
              handleDrawerAndClearTemplate={handleDrawerAndClearTemplate}
            />
          </ApplyTemplateFooter>
        </Col>
      </Row>
    </ViewWrapper>
  )
}

SelectPtoTemplateMode.propTypes = {
  serialNumber: PropTypes.string,
  onClose: PropTypes.func,
  isDrawerVisible: PropTypes.bool,
  setDrawerTitle: PropTypes.func,
  setDrawerState: PropTypes.func,
}

export default SelectPtoTemplateMode
